// Phase 6.3.1 — Toast primitive (provider + hook + viewport).
//
// Ephemeral notifications. <ToastProvider> wraps the app and renders
// a fixed bottom-right viewport; useToast() returns an enqueue
// function. Toasts auto-dismiss after `duration` (default 4s), can
// be dismissed manually (X), pause auto-dismiss on hover, and stack
// with a visible limit of 3 (the rest queue and appear as room frees
// up). Each card carries a status-colored left border.
//
// This replaces browser-default alert() and ad-hoc inline banners —
// 6.3.2 routes user-facing success/error feedback through here.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { AlertCircle, AlertTriangle, CheckCircle2, Info, X } from "lucide-react";

export type ToastType = "success" | "error" | "info" | "warning";

export interface ToastOptions {
  title: string;
  description?: string;
  type?: ToastType;
  /** Auto-dismiss delay in ms. Default 4000. */
  duration?: number;
}

interface ToastRecord extends Required<Omit<ToastOptions, "description">> {
  id: number;
  description?: string;
}

interface ToastContextValue {
  /** Enqueue a toast; returns its id (for manual dismiss). */
  toast: (opts: ToastOptions) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

/** Max simultaneously-rendered toasts; the rest wait in the queue. */
const MAX_VISIBLE = 3;
const DEFAULT_DURATION = 4000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback((opts: ToastOptions): number => {
    const id = nextId.current++;
    const record: ToastRecord = {
      id,
      title: opts.title,
      description: opts.description,
      type: opts.type ?? "info",
      duration: opts.duration ?? DEFAULT_DURATION,
    };
    setToasts((prev) => [...prev, record]);
    return id;
  }, []);

  const visible = toasts.slice(0, MAX_VISIBLE);

  return (
    <ToastContext.Provider value={{ toast, dismiss }}>
      {children}
      <div
        data-testid="toast-viewport"
        className="fixed bottom-loose right-loose z-[100] flex flex-col gap-snug pointer-events-none"
        aria-live="polite"
        aria-atomic="false"
      >
        {visible.map((t) => (
          <ToastCard key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

const TYPE_META: Record<
  ToastType,
  { border: string; icon: ReactNode }
> = {
  success: {
    border: "border-l-theme-success-text",
    icon: <CheckCircle2 className="w-4 h-4 text-theme-success-text" strokeWidth={1.5} aria-hidden />,
  },
  error: {
    border: "border-l-theme-error-border",
    icon: <AlertCircle className="w-4 h-4 text-theme-error-text" strokeWidth={1.5} aria-hidden />,
  },
  warning: {
    border: "border-l-theme-warning-text",
    icon: <AlertTriangle className="w-4 h-4 text-theme-warning-text" strokeWidth={1.5} aria-hidden />,
  },
  info: {
    border: "border-l-theme-info-text",
    icon: <Info className="w-4 h-4 text-theme-info-text" strokeWidth={1.5} aria-hidden />,
  },
};

function ToastCard({
  toast,
  onDismiss,
}: {
  toast: ToastRecord;
  onDismiss: () => void;
}) {
  const meta = TYPE_META[toast.type];
  const timerRef = useRef<number | null>(null);

  const clear = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const start = useCallback(() => {
    clear();
    timerRef.current = window.setTimeout(onDismiss, toast.duration);
  }, [clear, onDismiss, toast.duration]);

  // Start the auto-dismiss timer when this card becomes visible
  // (mounts). Because only the visible slice is rendered, a queued
  // toast's timer doesn't start until it scrolls into view.
  useEffect(() => {
    start();
    return clear;
  }, [start, clear]);

  return (
    <div
      role="status"
      data-testid="toast"
      data-type={toast.type}
      onMouseEnter={clear}
      onMouseLeave={start}
      className={[
        "pointer-events-auto w-80 max-w-[calc(100vw-2rem)]",
        "bg-theme-bg-elevated border border-theme-border-default border-l-2",
        "shadow-2xl px-default py-default flex items-start gap-snug",
        meta.border,
      ].join(" ")}
    >
      <div className="flex-shrink-0 mt-0.5">{meta.icon}</div>
      <div className="flex-1 min-w-0">
        <div data-testid="toast-title" className="text-body font-semibold">
          {toast.title}
        </div>
        {toast.description && (
          <div data-testid="toast-description" className="text-body-sm mt-0.5">
            {toast.description}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        data-testid="toast-dismiss"
        aria-label="Dismiss notification"
        className="flex-shrink-0 -mr-1 -mt-1 p-tight text-theme-text-tertiary hover:text-theme-text-primary transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-theme-focus-ring"
      >
        <X className="w-3.5 h-3.5" strokeWidth={1.5} aria-hidden />
      </button>
    </div>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (ctx === undefined) {
    throw new Error("useToast must be used inside a ToastProvider");
  }
  return ctx;
}
